import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { RootShareRecommendations } from './getShareRecommendations';
import { getShareRecommendations } from './getShareRecommendations';

export type {
	FacebookShareRecommendations,
	RedditShareRecommendations,
	RootShareRecommendations,
} from './getShareRecommendations';

/**
 * Empty-result polling: rec generation is async (kicked off by the petition.published event consumer
 * on the backend), so the dashboard may load before recs are persisted. Poll on empty results up to
 * 4 times at [5s, 5s, 10s, 10s] — same total ~30s budget as the previous implementation, but native
 * to react-query and visible in our NR telemetry via the dataUpdateCount on the active transaction.
 *
 * react-query's built-in `retry` handles errors; this handles successful-but-empty responses.
 */
const REFETCH_DELAYS = [5000, 5000, 10000, 10000] as const;

export function computeEmptyResultRefetchInterval(query: {
	state: {
		data: RootShareRecommendations | undefined;
		dataUpdateCount: number;
		status: 'pending' | 'error' | 'success';
	};
}): false | number {
	const { data, dataUpdateCount, status } = query.state;
	// Stop polling once the query is in an error state. TanStack Query retains the last
	// successful `data` across errors, so without this guard a fetch that succeeds with an
	// empty result and then fails on every refetch would poll forever (every 5s/10s).
	if (status === 'error') return false;
	if (!data) return false;
	const isEmpty = data.facebook.length === 0 && data.reddit.length === 0;
	if (!isEmpty) return false;
	if (dataUpdateCount < 1 || dataUpdateCount > REFETCH_DELAYS.length) return false;
	return REFETCH_DELAYS[dataUpdateCount - 1];
}

/**
 * Returns `true` while the query has produced at least one empty response and more retries
 * are still scheduled by `computeEmptyResultRefetchInterval`. Consumers (the wrapper hook,
 * primarily) use this to keep the UI in a loading state and to defer terminal telemetry
 * emission until polling settles — otherwise the user sees a flash of empty state and
 * dashboards see a double-emit (`empty` followed by `success`) for a single page load.
 */
export function isPollingForEmptyResults(result: UseQueryResult<RootShareRecommendations>): boolean {
	if (!result.data) return false;
	const isEmpty = result.data.facebook.length === 0 && result.data.reddit.length === 0;
	if (!isEmpty) return false;
	// `dataUpdateCount` is forwarded onto the runtime observer result from the underlying query
	// state but isn't surfaced on @tanstack/react-query's `UseQueryResult` union type — same field
	// used inside computeEmptyResultRefetchInterval, just read from the consumer-facing object.
	const { dataUpdateCount } = result as unknown as { dataUpdateCount: number };
	return dataUpdateCount > 0 && dataUpdateCount <= REFETCH_DELAYS.length;
}

export function useFetchShareRecommendations(slug: string, enabled: boolean): UseQueryResult<RootShareRecommendations> {
	const utilityContext = useUtilityContext();

	return useQuery({
		queryKey: ['share-recommendations', { slug }],
		queryFn: async () => getShareRecommendations(utilityContext, slug),
		enabled,
		refetchInterval: computeEmptyResultRefetchInterval,
		refetchIntervalInBackground: false,
	});
}
